import React, { useMemo, useState } from 'react';

import { Button } from '@cision/rover-ui';
import compact from 'lodash/compact';
import PropTypes from 'prop-types';

import { injectIntl } from 'react-intl';

import { useDispatch, useSelector } from 'react-redux';

import { TabMenuItem, TabMenu } from '@trendkite/ui';

import { isShareOfVoice } from 'components/widgetV3/utils';
import { DEV_FEATURES } from 'constants/constants';
import TranslatedMessage from 'i18n/TranslatedMessage';
import { userHasDevFeatureFlag } from 'services/feature-service/feature-service';

import widgetMessages from '../../widgetV3/widget-v3-date-range.messages';
import { DATA_ITEMS_PROPERTIES_MAP_V3 } from '../constants';
import Data from '../sections/data';
import DateRangeV3 from '../sections/date-range-v3';
import MetricV3 from '../sections/metric-v3';
import VisualizationTypeV3 from '../sections/visualization-type-v3';
import { getDateRangeCompareAllowedV3, STANDARD_SEARCH_LIMIT } from '../utils';

import { widgetWizardTabSelector } from '../widget-wizard-selectors';

import messages from './form.messages';
import { setUserIsSavingFiltersActionCreator } from './new-widget-filters-tab/new-widget-filters-reducer';

import WidgetFiltersTab from './new-widget-filters-tab/new-widget-filters-tab';
import styles from './steps.module.css';

export const SETTING_TAB = 0;
export const FILTERS_TAB = 1;

const CustomChartForm = ({
  nextStepCallback,
  dateRangeCompareType,
  onRemoveDataItem,
  dataLimit,
  dataItemsCount,
  selectedAnalyticId,
  widgetId,
  intl,
  onAddMetric,
  dateRangeV3,
  dateRangeCompareV3,
  dataSectionSourceType,
  showTrailing,
  primaryMetric,
  secondaryMetric,
  showFilters,
}) => {
  const defaultTab = useSelector(widgetWizardTabSelector);
  const [currentTab, setCurrentTab] = useState(defaultTab || SETTING_TAB);

  const userHasAccessUniversalFilters = userHasDevFeatureFlag(
    DEV_FEATURES.universalFiltersWidgetV3,
  );

  const canAddDateRangeCompare = getDateRangeCompareAllowedV3({
    primaryMetric,
    secondaryMetric,
    dataItemsCount,
  });

  const dispatch = useDispatch();

  const switchTabs = tabId => {
    setCurrentTab(tabId);
    dispatch(setUserIsSavingFiltersActionCreator(tabId === FILTERS_TAB));
  };
  const dataItemsProperties =
    DATA_ITEMS_PROPERTIES_MAP_V3[dataSectionSourceType];
  const selectedSearches = useSelector(dataItemsProperties.selector);

  const dateRangeDisabledMessage = useMemo(() => {
    const metrics = compact([primaryMetric, secondaryMetric]);

    if (isShareOfVoice(metrics) && !canAddDateRangeCompare) {
      const maxDateRangeSearches = Math.ceil(STANDARD_SEARCH_LIMIT / 2);
      return intl.formatMessage(
        messages.warningSecondaryDateOnlyAllowedWithXSearchesOrFewer,
        {
          NUMBER_OF_SEARCHES: maxDateRangeSearches,
        },
      );
    }
  }, [canAddDateRangeCompare, intl, primaryMetric, secondaryMetric]);

  return (
    <div className={styles.container}>
      {userHasAccessUniversalFilters && showFilters && (
        <div className={styles.tabContainer}>
          <TabMenu
            style={{ border: 0, padding: '20px 10px 0 10px', margin: 0 }}
          >
            <TabMenuItem>
              <Button
                data-qa="d27wP-4rwiVCWqQKfxpIp"
                className={[
                  styles.customTabButton,
                  currentTab === SETTING_TAB ? styles.tabActive : null,
                ]}
                onClick={() => switchTabs(SETTING_TAB)}
              >
                <TranslatedMessage {...messages.formSettingsTabTitle} />
              </Button>
            </TabMenuItem>
            <TabMenuItem>
              <Button
                data-qa="Wjae5Zxilb1JRRIaV4WZ9"
                className={[
                  styles.customTabButton,
                  currentTab === FILTERS_TAB ? styles.tabActive : null,
                ]}
                onClick={() => switchTabs(FILTERS_TAB)}
              >
                <TranslatedMessage {...messages.formFiltersTabTitle} />
              </Button>
            </TabMenuItem>
          </TabMenu>
        </div>
      )}
      {currentTab === SETTING_TAB && (
        <div className={styles.settingsContainer}>
          <MetricV3
            primaryMetric={primaryMetric}
            secondaryMetric={secondaryMetric}
            selectedAnalyticId={selectedAnalyticId}
            onAddMetric={onAddMetric}
            selectedItemsCount={dataItemsCount}
          />
          <Data
            nextStepCallback={nextStepCallback}
            dataLimit={dataLimit}
            sourceType={dataSectionSourceType}
            onRemoveItem={onRemoveDataItem}
            primaryMetric={primaryMetric}
            secondaryMetric={secondaryMetric}
          />
          <DateRangeV3
            canAddDateRangeCompare={canAddDateRangeCompare}
            dateRange={dateRangeV3}
            dateRangeCompareType={dateRangeCompareType}
            dateRangeCompare={dateRangeCompareV3}
            widgetId={widgetId}
            intl={intl}
            showTrailing={showTrailing}
            dateRangeLimitOneYear={
              primaryMetric?.visualizationType === 'WORD_CLOUD'
            }
            dateRangeLimitMessage={intl.formatMessage(
              widgetMessages.widgetWordCloudDateRangeLimit,
            )}
            iconButtonDisabledMessage={dateRangeDisabledMessage}
          />
          <VisualizationTypeV3
            nextStepCallback={nextStepCallback}
            primaryMetric={primaryMetric}
            secondaryMetric={secondaryMetric}
            intl={intl}
          />
        </div>
      )}
      {currentTab === FILTERS_TAB && (
        <WidgetFiltersTab
          widgetId={widgetId}
          searches={dataSectionSourceType === 'SEARCH' ? selectedSearches : []}
        />
      )}
    </div>
  );
};

CustomChartForm.propTypes = {
  nextStepCallback: PropTypes.func.isRequired,
  dateRangeV3: PropTypes.object.isRequired,
  dateRangeCompareType: PropTypes.string,
  dateRangeCompareV3: PropTypes.object,
  onRemoveDataItem: PropTypes.func.isRequired,
  dataLimit: PropTypes.number.isRequired,
  dataItemsCount: PropTypes.number.isRequired,
  selectedAnalyticId: PropTypes.number,
  title: PropTypes.string.isRequired,
  titleChangeCallback: PropTypes.func.isRequired,
  widgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  intl: PropTypes.object.isRequired,
  onAddMetric: PropTypes.func.isRequired,
  dataSectionSourceType: PropTypes.string.isRequired,
  showTrailing: PropTypes.bool,
  primaryMetric: PropTypes.object,
  secondaryMetric: PropTypes.object,
  showFilters: PropTypes.bool,
};

CustomChartForm.defaultProps = {
  dateRangeCompareType: null,
  hasKeyMessages: false,
  widgetId: null,
  dateRangeCompareV3: {},
  selectedAnalyticId: null,
  showTrailing: true,
  primaryMetric: null,
  secondaryMetric: null,
  showFilters: true,
};

export default injectIntl(CustomChartForm);
